/** Shared document upload rules — aligned with docs/MALICIOUS_FILE_UPLOAD.md */

export const UPLOAD_DOC_MAX_BYTES = 5 * 1024 * 1024; // 5MB

export const UPLOAD_DOC_ACCEPT = ".pdf,.jpeg,.jpg,.png";

export const UPLOAD_DOC_ALLOWED_EXTS = ["pdf", "jpg", "jpeg", "png"];

export const UPLOAD_DOC_ALLOWED_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

export const UPLOAD_DOC_ERROR_MESSAGE =
  "Invalid file type. Allowed: pdf, jpg, jpeg, png";

export const UPLOAD_DOC_SIZE_ERROR_MESSAGE = "File size must be under 5MB";

const DANGEROUS_INNER_EXTS = [
  "exe", "bat", "cmd", "com", "msi", "scr", "php", "phtml", "phar",
  "js", "mjs", "html", "htm", "shtml", "svg", "jsp", "asp", "aspx",
  "sh", "cgi", "py", "rb", "pl",
];

const hasDangerousName = (fileName) => {
  const name = String(fileName || "").trim();
  if (!name || /[\\/;\0]/.test(name)) {
    return true;
  }
  const parts = name.toLowerCase().split(".");
  if (parts.length < 2 || !parts[0] || !parts[parts.length - 1]) {
    return true;
  }
  // Allow extra dots in the base name (e.g. "16.22-2.pdf").
  // Block double extensions like invoice.pdf.exe / file.php.pdf
  const innerParts = parts.slice(0, -1);
  return innerParts.some((part) => DANGEROUS_INNER_EXTS.includes(part));
};

/**
 * Validate a user-selected document before upload.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export const validateUploadDocument = (file, options = {}) => {
  const maxBytes = options.maxBytes ?? UPLOAD_DOC_MAX_BYTES;

  if (!file) {
    return { ok: false, message: "Please select a file" };
  }

  if (hasDangerousName(file.name)) {
    return { ok: false, message: UPLOAD_DOC_ERROR_MESSAGE };
  }

  const ext = String(file.name).split(".").pop().toLowerCase();
  if (!UPLOAD_DOC_ALLOWED_EXTS.includes(ext)) {
    return { ok: false, message: UPLOAD_DOC_ERROR_MESSAGE };
  }

  if (file.type && !UPLOAD_DOC_ALLOWED_MIMES.includes(file.type)) {
    return { ok: false, message: UPLOAD_DOC_ERROR_MESSAGE };
  }

  if (file.size > maxBytes) {
    return { ok: false, message: UPLOAD_DOC_SIZE_ERROR_MESSAGE };
  }

  return { ok: true };
};

/** @returns {boolean} */
export const isAllowedUploadDocument = (file, options = {}) =>
  validateUploadDocument(file, options).ok;

/**
 * Validate every file in a FileList / array.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export const validateUploadFileList = (files, options = {}) => {
  const list = files ? Array.from(files) : [];
  for (const file of list) {
    const result = validateUploadDocument(file, options);
    if (!result.ok) {
      return result;
    }
  }
  return { ok: true };
};
